import type { CSSProperties } from 'react';

import type { SanityTestimonialSection } from '#/lib/sanity/queries/testimonialSection';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import {
  getSectionButtonTheme,
  getSectionThemeSurface,
  normalizeSectionTheme,
  type SectionTheme,
} from '#/lib/section-theme';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn } from '#/lib/utils';
import { Button } from '#/components/Button';

import {
  QuoteMark,
  TestimonialLogo,
  UeVerifiedBadge,
} from './TestimonialParts';

// Element colours sit on top of the shared section surface map.
const THEME_STYLES: Record<
  SectionTheme,
  {
    eyebrow: string;
    quoteMark?: string;
    quote: string;
    name: string;
    muted: string;
    ue: string;
    /** Fill colour for the masked customer-logo silhouette. */
    logo: string;
  }
> = {
  light: {
    eyebrow: 'text-cipher-05',
    // Lighter than the cipher-06 quote text, but still visible on the
    // light surface (the cipher-50 default washes out here).
    quoteMark: 'text-cipher-02',
    quote: 'text-cipher-06',
    name: 'text-charcoal',
    muted: 'text-grayscale-04',
    ue: 'text-charcoal',
    logo: 'bg-charcoal',
  },
  white: {
    eyebrow: 'text-cipher-05',
    quoteMark: 'text-cipher-02',
    quote: 'text-cipher-06',
    name: 'text-charcoal',
    muted: 'text-grayscale-04',
    ue: 'text-charcoal',
    logo: 'bg-charcoal',
  },
  purple: {
    eyebrow: 'text-electric',
    quoteMark: 'text-cipher-03',
    quote: 'text-cloud',
    name: 'text-cloud',
    muted: 'text-cloud',
    ue: 'text-cloud',
    logo: 'bg-cloud',
  },
  charcoal: {
    eyebrow: 'text-electric',
    // The QuoteMark default (cipher-50 lavender) reads well on charcoal too;
    // electric was too loud here.
    quoteMark: undefined,
    quote: 'text-cloud',
    name: 'text-cloud',
    muted: 'text-grayscale-03',
    ue: 'text-cloud',
    logo: 'bg-cloud',
  },
};

export function TestimonialSection(
  props: SanityTestimonialSection & PageLayoutSharedProps,
) {
  const ueUrl = props.ueUrl ? new URL(props.ueUrl) : null;
  const theme = normalizeSectionTheme(props.theme);
  const styles = THEME_STYLES[theme];

  return (
    <div className={cn('p-main py-40 l:py-100', getSectionThemeSurface(theme))}>
      <div className="fade-in mx-auto flex w-full max-w-[890px] flex-col items-start gap-24 l:gap-40">
        {props.eyebrow ? (
          <span
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'eyebrow',
            )}
            className={cn('type-eyebrow block', styles.eyebrow)}
          >
            {props.eyebrow}
          </span>
        ) : null}

        <QuoteMark
          className={cn('w-40 shrink-0 m:w-50 l:w-67', styles.quoteMark)}
        />

        <p
          data-sanity={createSanityFieldDataAttribute(
            props.sanitySource,
            'quote',
          )}
          className={cn(
            'type-heading6 m:type-heading5 l:type-heading4 text-pretty',
            styles.quote,
          )}
        >
          “{props.quote}”
        </p>

        <div className="flex w-full flex-col gap-36 m:flex-row m:flex-wrap m:items-center m:justify-between m:gap-x-20 m:gap-y-36">
          <div className="flex flex-col items-start gap-12 m:flex-row m:items-center m:gap-20">
            {props.image?.url ? (
              <TestimonialLogo
                image={props.image}
                className={cn(
                  'w-[min(calc(var(--logo-h)*var(--logo-ratio)),var(--logo-max-w,9999px))] [--logo-h:32px] m:[--logo-h:66px]',
                  styles.logo,
                )}
                style={
                  {
                    '--logo-ratio': props.image.width / props.image.height,
                    ...(props.logoMaxWidth
                      ? { '--logo-max-w': `${props.logoMaxWidth}px` }
                      : null),
                  } as CSSProperties
                }
              />
            ) : null}
            <div className="space-y-6">
              <p
                className={cn(
                  'type-paragraph-lg font-medium trim-text',
                  styles.name,
                )}
              >
                {props.name}
              </p>
              {props.role ? (
                <p className={cn('type-paragraph-sm trim-text', styles.muted)}>
                  {props.role}
                </p>
              ) : null}
            </div>
          </div>

          {ueUrl ? (
            <a
              className={cn(
                'flex flex-col items-start gap-12 transition-opacity hover:opacity-70 m:flex-row m:items-start m:gap-20',
                styles.ue,
              )}
              href={ueUrl.toString()}
              target="_blank"
              rel="noopener noreferrer"
            >
              <UeVerifiedBadge className="size-32 shrink-0 m:size-48" />
              <div className="space-y-6">
                <p className="type-paragraph-lg font-medium trim-text">
                  UE Verified
                </p>
                <p className={cn('type-paragraph-sm trim-text', styles.muted)}>
                  {ueUrl.hostname + ueUrl.pathname}
                </p>
              </div>
            </a>
          ) : null}
        </div>

        {props.cta ? (
          <Button
            href={props.cta.href}
            target={props.cta.target}
            variant="secondary"
            theme={getSectionButtonTheme(theme)}
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'cta',
            )}
          >
            {props.cta.title}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
